import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	useRef,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	SypacButton,
	SypacIcon,
	SypacText,
} from '@sypac/component-library-react';
import { useGetCategories } from '../../../hooks/use-get-categories';
import '../select-product.scss';
import { QuantityPickerContext } from '../../../context/QuantityPickerContext/quantity-picker.context';
import QuantityPicker from '../../../components/QuantityPicker/QuantityPicker';
import { OrderContext } from '../../../context/OrderContext/order.context';
import { COUNTERS } from '../constants';
import {
	ProductResponse,
	ProductService,
} from '../../../services/product.services';
import { T, useTranslate } from '@tolgee/react';
import ClientStepper from '../../../components/ClientStepper/ClientStepper';
import ClientOrderSummary from '../../../components/ClientOrderSummary/ClientOrderSummary';
import { OrdersService, Prices } from '../../../services/orders.services';
import { GeoService } from '../../../services/geo.services';
import ClientStoreBreadcrumbs from '../../../components/ClientStoreBreadcrumbs/ClientStoreBreadcrumbs';
import { CompanyService } from '../../../services/company.services';
import { toastVariant } from '../../../components/ToastVariant/toastVariant';
import {
	CUSTOMER_STORE_FLOW,
	OrderCreationFlow,
} from '../../../types/order-flow';
import LayeredProgressBar from '../../../components/LayeredProgressBar/LayeredProgressBar';
import { OrderInterface } from '../../../components/OrdersTable/interfaces/Order.interface';

const ClientSelectQuantity: React.FC = () => {
	const { t } = useTranslate();
	const navigate = useNavigate();
	const { order, setOrder, additional } = useContext(OrderContext);
	const { totalQuantityCounter, inputQuantity } = useContext(
		QuantityPickerContext,
	);
	const { groupId, categoryId, productId } = useParams();
	const [userCountry, setUserCountry] = useState<string>();
	const [categories] = useGetCategories({
		ids: [categoryId!],
		countryCode: userCountry,
	});
	const [groups] = useGetCategories({
		countryCode: userCountry,
	});
	const [products, setProducts] = useState<ProductResponse>({
		items: [],
		count: 0,
	});
	const [quantity, setQuantity] = useState(0);
	const [prices, setPrices] = useState<Prices>({
		productPriceForClient: 0,
		productPriceForClientWithVat: 0,
		transportPriceForClient: 0,
		transportPriceForClientWithVat: 0,
	});

	const [loadingPrices, setLoadingPrices] = useState<boolean>(false);
	const requestIdRef = useRef(0);
	const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

	const flow: OrderCreationFlow =
		additional?.orderFlow ?? CUSTOMER_STORE_FLOW.BASE;
	const stepNo = flow === CUSTOMER_STORE_FLOW.CONTRACT_LOCATION_FIRST ? 2 : 1;

	const getMyCompanyCallback = useCallback(async () => {
		try {
			const { data } = await CompanyService.getMeCompany();
			setUserCountry(
				data.countryCode === 'MD'
					? 'MDA'
					: data.countryCode === 'PL'
					? 'POL'
					: '',
			);
		} catch (error) {
			toastVariant(`Something went wrong. ${error?.toString()}`, true);
		}
	}, []);

	useEffect(() => {
		getMyCompanyCallback().then(() => {});
	}, [getMyCompanyCallback]);

	const getProducts = useCallback(async () => {
		try {
			const resp = await ProductService.getList({
				status: 'all',
				categoryId,
				limit: 100,
				offset: 0,
				role: 'customer',
			});
			setProducts(resp.data);
		} catch (e) {}
	}, [categoryId]);

	useEffect(() => {
		getProducts().then(() => {});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [categoryId]);

	useEffect(() => {
		if (totalQuantityCounter?.length) {
			const result = totalQuantityCounter?.reduce(
				(total, item) => total + item.payload * item.quantity,
				0,
			);
			setQuantity(result);
		} else {
			setQuantity(0);
		}
	}, [totalQuantityCounter]);

	useEffect(() => {
		if (inputQuantity) {
			setQuantity(inputQuantity);
		}
	}, [inputQuantity]);

	const currentProduct = useMemo(() => {
		return products?.items?.find((item) => item.id === +productId!);
	}, [products, productId]);
	const currentCategory = useMemo(() => {
		return categories?.items?.find((item) => item.id === categoryId);
	}, [categories, categoryId]);
	const currentGroup = useMemo(() => {
		return groups?.items?.find((item) => item.id === groupId);
	}, [groups, groupId]);

	const isContractOrder = Boolean(order?.contractData);

	const selectedContractLine = useMemo(() => {
		if (!isContractOrder || !order?.contractData?.lines?.length || !productId) {
			return undefined;
		}
		const pid = Number(productId);
		return order.contractData.lines.find(
			(l: any) => (l?.product?.id ?? l?.productId) === pid,
		);
	}, [isContractOrder, order?.contractData, productId]);

	const { orderedQtyTons, deliveredQtyTons } = useMemo(() => {
		const contractOrders = (order?.contractData?.orders ??
			[]) as OrderInterface[];

		if (!contractOrders.length) {
			return {
				orderedQtyTons: Number(selectedContractLine?.orderedQtyTons ?? 0),
				deliveredQtyTons: Number(selectedContractLine?.deliveredQtyTons ?? 0),
			};
		}

		let ordered = 0;
		let delivered = 0;

		for (const o of contractOrders) {
			const trucks = Array.isArray(o?.trucks) ? o.trucks : [];

			for (const truck of trucks) {
				const hasLoadedQuantity =
					truck?.loadedQuantity !== null &&
					truck?.loadedQuantity !== undefined &&
					!Number.isNaN(Number(truck.loadedQuantity));

				if (hasLoadedQuantity) {
					delivered += Number(truck.loadedQuantity) || 0;
				} else {
					ordered += Number(truck?.payload ?? 0) || 0;
				}
			}
		}

		return { orderedQtyTons: ordered, deliveredQtyTons: delivered };
	}, [
		order?.contractData?.orders,
		selectedContractLine?.orderedQtyTons,
		selectedContractLine?.deliveredQtyTons,
	]);

	const allowedOverdraftTons = useMemo(() => {
		const pct = Number(selectedContractLine?.allowedOverdraftPercent);
		const contractQty = Number(selectedContractLine?.contractQtyTons ?? 0);

		if (!pct || pct <= 0 || !contractQty) return undefined;

		return (contractQty * pct) / 100;
	}, [
		selectedContractLine?.allowedOverdraftPercent,
		selectedContractLine?.contractQtyTons,
	]);

	const availableContractQuantity = useMemo(() => {
		if (!isContractOrder || !selectedContractLine?.contractQtyTons) {
			return undefined;
		}

		const contractQty = Number(selectedContractLine.contractQtyTons ?? 0);
		const overdraft = Number(allowedOverdraftTons ?? 0);
		const usedQty = Number(orderedQtyTons) + Number(deliveredQtyTons);

		const maxQtyWithOverdraft = contractQty + overdraft;

		return Math.max(maxQtyWithOverdraft - usedQty, 0);
	}, [
		isContractOrder,
		selectedContractLine?.contractQtyTons,
		allowedOverdraftTons,
		orderedQtyTons,
		deliveredQtyTons,
	]);

	const derivedContractPrices = useMemo(() => {
		if (!selectedContractLine) return undefined;

		const base = Number(selectedContractLine.pricePerTonBuyer ?? 0);
		const markupPct = Number(selectedContractLine.markup ?? 0);
		const vatPct = Number(
			selectedContractLine?.product?.vat ?? currentProduct?.vat ?? 0,
		);

		const pricePerUnit = base;
		const pricePerUnitWithProcent = +(base * (1 + markupPct / 100)).toFixed(2);
		const pricePerUnitWithVat = +(
			pricePerUnitWithProcent *
			(1 + vatPct / 100)
		).toFixed(2);

		return {
			pricePerUnit,
			pricePerUnitWithProcent,
			pricePerUnitWithVat,
		};
	}, [selectedContractLine, currentProduct?.vat]);

	const effectiveProduct = useMemo(() => {
		if (selectedContractLine?.product && derivedContractPrices) {
			return {
				...selectedContractLine.product,
				pricePerUnit: derivedContractPrices.pricePerUnit,
				pricePerUnitWithProcent: derivedContractPrices.pricePerUnitWithProcent,
				pricePerUnitWithVat: derivedContractPrices.pricePerUnitWithVat,
			};
		}
		return currentProduct;
	}, [selectedContractLine?.product, derivedContractPrices, currentProduct]);

	const getPrices = useCallback(
		async (currentRequestId: number) => {
			try {
				if (!effectiveProduct?.id) {
					return;
				}
				let calcQuantity: any = 0;
				let trucksNr: any = 1;
				if (totalQuantityCounter?.length) {
					calcQuantity = totalQuantityCounter?.reduce(
						(total: any, item) => total + item.payload * item.quantity,
						0,
					);
					trucksNr = totalQuantityCounter?.reduce(
						(total: any, item) => total + item.quantity,
						0,
					);
				}
				if (inputQuantity) {
					calcQuantity = inputQuantity;
				}
				let latDelivery = 0;
				let longDelivery = 0;

				if (order?.location?.locationId) {
					const resp = await GeoService.getLocationDetails({
						locationid: order?.location?.locationId,
					});
					latDelivery = resp?.data?.Latitude;
					longDelivery = resp?.data?.Longitude;
				} else if (isContractOrder && order?.location) {
					latDelivery = order.location.latitude!;
					longDelivery = order.location.longitude!;
				}

				const { data } = await OrdersService.calculatePrice({
					quantity: calcQuantity,
					trucksNr,
					trucks:
						!inputQuantity && totalQuantityCounter?.length
							? totalQuantityCounter.map(({ payload, quantity }) => ({
									payload: Number(payload),
									quantity: Number(quantity),
							  }))
							: undefined,
					latPickup: effectiveProduct?.location?.coordinates?.length
						? effectiveProduct.location.coordinates[1]
						: 47.04014,
					longPickup: effectiveProduct?.location?.coordinates?.length
						? effectiveProduct.location.coordinates[0]
						: 28.80058,
					latDelivery,
					longDelivery,
					productId: effectiveProduct?.id!,
					...(isContractOrder
						? { contractLineId: order?.contractData?.lines?.[0]?.id }
						: {}),
				});

				if (currentRequestId === requestIdRef.current) {
					setPrices(data);
					setLoadingPrices(false);
				}
			} catch (e) {
				if (currentRequestId === requestIdRef.current) setLoadingPrices(false);
			}
		},
		[
			effectiveProduct,
			inputQuantity,
			order,
			isContractOrder,
			totalQuantityCounter,
		],
	);

	useEffect(() => {
		if (!effectiveProduct?.id) return;

		requestIdRef.current += 1;
		const currentId = requestIdRef.current;
		setLoadingPrices(true);

		if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

		debounceTimerRef.current = setTimeout(() => {
			void getPrices(currentId);
		}, 400);

		return () => {
			if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
		};
	}, [getPrices, effectiveProduct?.id, quantity]);

	const next = () => {
		const newOrder = {
			...order,
			product: effectiveProduct,
			category: currentCategory,
			group: currentGroup,
		};
		setOrder({
			...newOrder,
		});
		navigate(
			`/store/groups/${groupId}/categories/${categoryId}/products/${productId}/date`,
		);
	};

	return (
		<div className="w-full flex flex-col gap-4 h-[calc(100vh-60px)] gap-x-2.5 overflow-x-hidden xl:overflow-x-auto mt-[68px] sm:mt-0">
			<div className="w-[calc(100vw-26px)] sm:w-[1102px] xl:self-center flex flex-col gap-4 pt-3.5 sm:pt-0 mb-1 xl-2xl:mb-4 px-3.5 sm:px-12 xl:ml-0">
				<ClientStoreBreadcrumbs
					currentCategory={currentCategory}
					currentGroup={currentGroup}
					currentProduct={effectiveProduct}
					categoryId={categoryId}
					contract={isContractOrder ? order?.contractData : undefined}
				/>
			</div>
			<div className="border-0 border-solid border-t-[1px] border-gray-10" />

			<div className="w-[calc(100vw-26px)] sm:w-[1102px] xl:self-center px-3.5 sm:px-12 xl:ml-0">
				<ClientStepper currentStep={stepNo} flow={flow} />
			</div>
			<div className="border-0 border-solid border-t-[1px] border-gray-10" />

			<div className="sm:w-[calc(100vw-154px)] xl:w-[1133px] xl:self-center flex flex-col sm:flex-row gap-6 sm:justify-between mb-6 sm:mb-0 mt-2 xl-2xl:mt-3.5 sm:ml-12 sm:mr-7.5 xl:mx-0">
				<div className="w-[calc(100vw-26px)] sm:w-fit sm:max-w-[520px] flex-grow flex flex-col gap-5 ml-3.5 sm:ml-0">
					{order?.contractData && (
						<>
							<LayeredProgressBar
								type={{
									key: 'clientTotalContractQuantity',
									label: 'Total contract quantity',
								}}
								totalTons={selectedContractLine?.contractQtyTons ?? 0}
								segments={[
									{
										value: orderedQtyTons + deliveredQtyTons,
										colorClass: 'bg-mountain-meadow',
										tooltip: `${t('layeredProgressBar.placed', 'Placed:')} ${
											orderedQtyTons + deliveredQtyTons
										} t`,
										enableBrightnessHover: true,
									},
									{
										value: quantity,
										colorClass: 'bg-texas-rose',
										tooltip: `${t(
											'layeredProgressBar.ordered',
											'Ordered:',
										)} ${quantity} t`,
										enableBrightnessHover: true,
									},
								]}
								allowedOverdraftTons={allowedOverdraftTons}
								valueSuffix=" t"
								showSummary
							/>
							<div className="border-0 border-solid border-t-[1px] border-gray-10" />
						</>
					)}

					<QuantityPicker
						counters={[...COUNTERS].sort((a, b) => b.payload - a.payload)}
						availableQuantity={
							isContractOrder && availableContractQuantity != null
								? availableContractQuantity
								: effectiveProduct?.availableQuantity!
						}
						unlimited={isContractOrder ? false : effectiveProduct?.unlimited!}
					/>

					<nav className="hidden sm:flex gap-5 justify-center mt-auto">
						<SypacButton variant="secondary" size="small" className="w-full">
							<button
								type="button"
								className="flex flex-row gap-2 px-6 py-[13px] rounded-lg w-full transition"
								onClick={() => {
									navigate(-1);
								}}
							>
								<SypacIcon
									size="custom"
									width="10px"
									height="12px"
									icon-name="Alt Arrow Left"
								/>
								<SypacText variant="body-normal-medium">
									<p className="mt-[3px]">
										<T keyName="clientSelectQuantity.back">Back</T>
									</p>
								</SypacText>
							</button>
						</SypacButton>
						<SypacButton className="w-full" variant="secondary" size="small">
							<button
								type="button"
								className="flex flex-row gap-2 px-6 py-[13px] rounded-lg w-full transition"
								onClick={() => next()}
								disabled={quantity === 0}
							>
								<SypacText variant="body-normal-medium">
									<p className="mt-[3px]">
										<T keyName="clientSelectQuantity.next">Next</T>
									</p>
								</SypacText>
								<SypacIcon
									size="custom"
									width="10px"
									height="12px"
									icon-name="Alt Arrow Right"
								/>
							</button>
						</SypacButton>
					</nav>
				</div>

				<aside className="sm:w-[412px] xl:w-[460px] inline-table">
					<ClientOrderSummary
						currentProduct={effectiveProduct}
						prices={prices}
						quantity={quantity}
						step={stepNo}
						loading={loadingPrices}
					/>
				</aside>

				<footer className="w-[calc(100vw-26px)] flex flex-col sm:hidden gap-5 justify-center ml-3.5">
					<SypacButton className="w-full" variant="secondary" size="small">
						<button
							type="button"
							className="flex flex-row gap-2 px-6 py-[13px] rounded-lg w-full transition"
							onClick={() => next()}
							disabled={quantity === 0}
						>
							<SypacText variant="body-normal-medium">
								<p className="mt-[3px]">
									<T keyName="clientSelectQuantity.next">Next</T>
								</p>
							</SypacText>
							<SypacIcon
								size="custom"
								width="10px"
								height="12px"
								icon-name="Alt Arrow Right"
							/>
						</button>
					</SypacButton>
					<SypacButton variant="secondary" size="small" className="w-full">
						<button
							type="button"
							className="flex flex-row gap-2 px-6 py-[13px] rounded-lg w-full transition"
							onClick={() => {
								navigate(-1);
							}}
						>
							<SypacIcon
								size="custom"
								width="10px"
								height="12px"
								icon-name="Alt Arrow Left"
							/>
							<SypacText variant="body-normal-medium">
								<p className="mt-[3px]">
									<T keyName="clientSelectQuantity.back">Back</T>
								</p>
							</SypacText>
						</button>
					</SypacButton>
				</footer>
			</div>
		</div>
	);
};

export default ClientSelectQuantity;
